import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import ContactFormModal from '../components/ContactFormModal';
import Reveal from '../components/Reveal';
import i18n from '../i18n';
import pic5 from '../assets/FusionX2.png';

const partnerBenefits = [
  {
    title: 'Stop Selling Impressions. Start Selling Results.',
    body: 'Move beyond vanity metrics. Offer your clients what they really want: measurable foot traffic and a direct link between digital campaigns and in-store sales. Become the agency that proves its value in the real world.',
  },
  {
    title: 'Build a Predictable, Recurring Revenue Stream',
    body: 'Escape the feast-or-famine project cycle. With our tiered commission structure, you’ll earn compounding, recurring revenue on the lifetime value of your clients, building a more stable and scalable P&L.',
  },
  {
    title: 'Differentiate Your Agency in a Crowded Market',
    body: 'While other agencies are stuck offering the same old services, you’ll bring a truly innovative “phygital” solution. Win more pitches, increase client retention, and position your agency as a leader in the next wave of marketing.',
  },
];

const howItWorksSteps = [
  {
    step: 1,
    title: 'Get Certified, Fast',
    body: 'Our self-paced "Play2Visit Fundamentals" course is designed for busy strategists. You’ll learn the platform and sales playbook in just a few hours.',
  },
  {
    step: 2,
    title: 'Launch Your First Campaign',
    body: 'Using our sales toolkit and co-marketing assets, you’ll be equipped to sign and onboard your first client with expert support from our team.',
  },
  {
    step: 3,
    title: 'Scale & Earn',
    body: 'As you grow your client base, you’ll unlock higher commission tiers, dedicated support, and even a seat on our Partner Advisory Council.',
  },
];

const testimonials = [
  {
    quote: "Play2Visit transformed how we approach client campaigns. We're not just driving traffic; we're proving ROI with actual footfall. Our clients are thrilled, and so are our retention rates!",
    author: "Jane Doe",
    title: "CEO, Marketing Mavericks",
  },
  {
    quote: "The recurring commission model is a game-changer. It's allowed us to build a more predictable and scalable business. The support from the Play2Visit team is exceptional.",
    author: "John Smith",
    title: "Founder, Growth Gurus",
  },
  {
    quote: "Integrating Play2Visit into our offerings has differentiated us significantly. We're winning pitches against larger agencies because we offer something truly innovative.",
    author: "Emily White",
    title: "Director of Strategy, Brand Builders",
  },
];

const clientLogos = [
  { name: "Client A", src: "/logo1.png" },
  { name: "Client B", src: "/logo2.png" },
  { name: "Client C", src: "/logo3.png" },
  { name: "Client D", src: "/logo4.png" },
  { name: "Client E", src: "/logo5.png" },
];

const tiers = [
  {
    name: 'Authorized Reseller',
    description: 'Your on-ramp to launching Play2Visit for clients within weeks.',
    requirements: ['Complete Play2Visit Fundamentals training'],
    benefits: [
      '15% commission on first-year contract value',
      'Authorized Reseller badge and toolkit',
      'Access to pitch decks, one-pagers, case studies',
      'Guided path toward Certified Partner status',
    ],
    quote:
      '“Ready to innovate your agency’s offering? Earn 15% today and unlock the path to a 25% recurring revenue engine.”',
  },
  {
    name: 'Certified Partner',
    description: 'For teams that make measurable footfall the core of their service.',
    requirements: [
      '3+ active campaigns at any time',
      '5%+ player-to-visitor conversion rate',
      '80% client retention over 12 months',
      'At least one Play2Visit Campaign Strategist certified',
    ],
    benefits: [
      '25% recurring commission on lifetime value',
      'Certified badge plus Partner Directory leads',
      'Dedicated partner support channel',
      'Full analytics dashboard access',
    ],
    quote:
      '“Don’t just promise engagement—prove it with store visits and a 25% recurring revenue stream.”',
  },
  {
    name: 'Premier Partner',
    description: 'Invitation-only alliance for top performers influencing the roadmap.',
    requirements: [
      '$150k+ Annual Play2Visit Client Contract Value',
      '10%+ player-to-visitor conversion rate',
      '90% client retention and annual co-marketing',
    ],
    benefits: [
      '35%+ custom commission structure with bonuses',
      'Dedicated Partner Manager and strategy pods',
      'Joint marketing, events, and spotlight case studies',
      'Seat on the Partner Advisory Council',
    ],
    quote:
      '“You’re a phygital category leader—shape the future with co-marketing, roadmap access, and the richest rewards.”',
  },
];

const onboardingModules = [
  { title: 'The phygital revolution', detail: 'Mission, market shift, and why foot traffic matters again.' },
  { title: 'Inside the Play2Visit platform', detail: 'FusionX tournaments, embeds, earned ad moments, integrations.' },
  { title: 'Ideal customer profile', detail: 'Retail, hospitality, brand activations, and how to qualify demand.' },
  { title: 'Selling Play2Visit', detail: 'Value props, objection handling, and the agency sales toolkit.' },
  { title: 'Launching campaign one', detail: 'Onboarding checklist, success benchmarks, and reporting rhythms.' },
  { title: 'Your path to Certified', detail: 'Performance metrics, retention plays, and scaling to recurring revenue.' },
];

const analyticsHighlights = [
  {
    title: 'Engagement funnel clarity',
    bullets: ['Player acquisition by channel', 'Active vs. returning players', 'Leaderboard health & playtime'],
  },
  {
    title: 'Phygital conversion proof',
    bullets: ['Store visits & QR check-ins', 'Player-to-visitor conversion rate', 'Reward redemptions & uplift'],
  },
  {
    title: 'Brand & ad monetization',
    bullets: ['Earned ad moments viewed', 'Campaign-level ROI snapshots', 'Future-ready brand lift modules'],
  },
];

const partnerFaqs = [
  {
    q: 'Who is the ideal partner for this program?',
    a: 'Our program is designed for marketing agencies, consultants, and service providers who have clients with physical locations, such as retail stores, restaurants, and event venues. If your clients need to drive foot traffic, we can help.',
  },
  {
    q: 'How does the commission structure actually work?',
    a: 'You start as an Authorized Reseller earning 15% on the first-year contract value. As you meet performance benchmarks, you graduate to Certified Partner status, where you earn a 25% recurring commission on the entire lifetime value of the client. Premier Partners unlock even higher, custom rates.',
  },
  {
    q: 'What kind of support and resources do you provide?',
    a: 'We provide everything you need to succeed: comprehensive online training, sales playbooks, co-branded marketing assets, case studies, and a dedicated partner support channel. Certified Partners and above get access to even more resources, including leads from our directory.',
  },
  {
    q: 'Can we white-label the Play2Visit platform?',
    a: 'While the core platform is branded as Play2Visit, we offer extensive co-branding opportunities. Your clients will see you as the strategic provider, and we work with you to ensure our platform feels like a natural extension of your services.',
  },
];

const Partners = () => {
  const { lng } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    if (lng && i18n.language !== lng) {
      i18n.changeLanguage(lng);
    }
  }, [lng]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="bg-gradient-to-br from-gray-950 via-gray-900 to-black text-white min-h-screen overflow-x-hidden">
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-purple-900/40 to-transparent blur-3xl opacity-60 pointer-events-none" />
        <div className="container mx-auto px-4 md:px-8 text-center relative">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.4em] text-purple-300">The New Agency Playbook</p>
          </Reveal>
          <Reveal>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight text-white mt-4">
              Stop Selling Clicks.
              <br />
              Start Delivering <span className="text-pink-400">Foot Traffic.</span>
            </h1>
          </Reveal>
          <Reveal>
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mt-6">
              Tired of fighting over shrinking CPC budgets and struggling to prove ROI? Partner with Play2Visit to offer a service no one else can: turning digital engagement into real, measurable store visits for your clients.
            </p>
          </Reveal>
          <Reveal>
            <div className="flex flex-wrap justify-center gap-4 mt-8">
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {partnerBenefits.map((prop) => (
              <Reveal key={prop.title} direction="up">
                <div className="bg-gray-900/60 border border-white/5 rounded-2xl p-6 text-center h-full">
                  <p className="text-xl font-semibold text-white mb-2">{prop.title}</p>
                  <p className="text-gray-300">{prop.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-gray-950/60 border-y border-white/5">
        <div className="container mx-auto px-4 md:px-8 space-y-12">
          <Reveal direction="up">
            <div className="text-center max-w-3xl mx-auto space-y-3">
              <p className="text-sm uppercase tracking-[0.4em] text-purple-300">How It Works</p>
              <h2 className="text-3xl md:text-4xl font-bold">Your Path to Recurring Revenue in 3 Steps</h2>
            </div>
          </Reveal>
          <div className="relative max-w-4xl mx-auto">
            <div className="absolute top-8 left-0 w-full h-0.5 bg-white/10 hidden md:block" />
            <Reveal direction="up">
              <div className="relative grid gap-10 md:grid-cols-3">
                {howItWorksSteps.map((step) => (
                  <div key={step.step} className="relative text-center md:text-left">
                    <div className="flex justify-center md:justify-start">
                      <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-purple-600 to-pink-600 rounded-full border-2 border-white/20 font-bold text-2xl shadow-lg">
                        {step.step}
                      </div>
                    </div>
                    <p className="font-semibold text-xl text-white mt-8">{step.title}</p>
                    <p className="text-gray-300 mt-2">{step.body}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-gray-950/60 border-y border-white/5">
        <div className="container mx-auto px-4 md:px-8 space-y-12">
          <Reveal direction="up">
            <div className="text-center max-w-3xl mx-auto space-y-3">
              <p className="text-sm uppercase tracking-[0.4em] text-purple-300">Partner tiers</p>
              <h2 className="text-3xl md:text-4xl font-bold">A clear path from reseller to strategic ally</h2>
              <p className="text-lg text-gray-300">
                Start with the fundamentals, unlock recurring revenue at Certified, and step onto the Advisory Council as a Premier Partner.
              </p>
            </div>
          </Reveal>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {tiers.map((tier, index) => (
              <Reveal key={tier.name} direction={index === 1 ? 'up' : index === 0 ? 'left' : 'right'}>
                <div className="h-full bg-gray-900/60 border border-white/10 rounded-3xl p-6 flex flex-col space-y-5 shadow-xl">
                  <div>
                    <p className="text-xs uppercase tracking-[0.5em] text-purple-200">{tier.name}</p>
                    <p className="text-xl font-semibold text-white mt-2">{tier.description}</p>
                  </div>
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-gray-400 uppercase tracking-[0.35em]">Requirements</p>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      {tier.requirements.map((req) => (
                        <li key={req} className="flex items-start gap-2">
                          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-purple-300" />
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-gray-400 uppercase tracking-[0.35em]">Benefits</p>
                    <ul className="space-y-2 text-gray-100 text-sm">
                      {tier.benefits.map((benefit) => (
                        <li key={benefit} className="flex items-start gap-2">
                          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-pink-300" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-sm text-purple-200 italic flex-1">{tier.quote}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/*
      <section className="py-20 md:py-24 bg-gray-950/60 border-y border-white/5">
        <div className="container mx-auto px-4 md:px-8 space-y-12">
          <Reveal direction="up">
            <div className="text-center max-w-3xl mx-auto space-y-3">
              <p className="text-sm uppercase tracking-[0.4em] text-purple-300">What Our Partners Say</p>
              <h2 className="text-3xl md:text-4xl font-bold">Hear directly from agencies driving real results</h2>
            </div>
          </Reveal>
          <div className="grid gap-8 md:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <Reveal key={index} direction="up">
                <div className="bg-gray-900/60 border border-white/10 rounded-3xl p-6 flex flex-col h-full shadow-xl">
                  <p className="text-lg text-gray-200 flex-1">"{testimonial.quote}"</p>
                  <div className="mt-6 border-t border-white/10 pt-4">
                    <p className="font-semibold text-white">{testimonial.author}</p>
                    <p className="text-sm text-gray-400">{testimonial.title}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      */}

      {/*
      <section className="py-20 md:py-24 bg-gray-950/60 border-y border-white/5">
        <div className="container mx-auto px-4 md:px-8 space-y-12">
          <Reveal direction="up">
            <div className="text-center max-w-3xl mx-auto space-y-3">
              <p className="text-sm uppercase tracking-[0.4em] text-purple-300">What Our Partners Say</p>
              <h2 className="text-3xl md:text-4xl font-bold">Hear directly from agencies driving real results</h2>
            </div>
          </Reveal>
          <div className="grid gap-8 md:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <Reveal key={index} direction="up">
                <div className="bg-gray-900/60 border border-white/10 rounded-3xl p-6 flex flex-col h-full shadow-xl">
                  <p className="text-lg text-gray-200 flex-1">"{testimonial.quote}"</p>
                  <div className="mt-6 border-t border-white/10 pt-4">
                    <p className="font-semibold text-white">{testimonial.author}</p>
                    <p className="text-sm text-gray-400">{testimonial.title}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      */}

      {/*
      <section className="py-20 md:py-24 bg-gray-950/60 border-y border-white/5">
        <div className="container mx-auto px-4 md:px-8 space-y-12">
          <Reveal direction="up">
            <div className="text-center max-w-3xl mx-auto space-y-3">
              <p className="text-sm uppercase tracking-[0.4em] text-purple-300">Powering Campaigns For Brands Like...</p>
              <h2 className="text-3xl md:text-4xl font-bold">Trusted by leading brands to drive foot traffic</h2>
            </div>
          </Reveal>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {clientLogos.map((logo, index) => (
              <Reveal key={index} direction="up" delay={index * 0.1}>
                <img src={logo.src} alt={logo.name} className="h-12 md:h-16 w-auto opacity-70 hover:opacity-100 transition-opacity duration-300 filter grayscale hover:grayscale-0" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      */}

      <section className="py-20 md:py-24">
        <div className="container mx-auto px-4 md:px-8 space-y-12">
          <Reveal direction="up">
            <div className="text-center space-y-3 max-w-2xl mx-auto">
              <p className="text-sm uppercase tracking-[0.4em] text-purple-300">A Curriculum Designed for Your Success</p>
              <h2 className="text-3xl md:text-4xl font-bold">Your Path to Phygital Expertise</h2>
              <p className="text-lg text-gray-300">
                “Play2Visit Fundamentals” is a modular course you can complete between client calls. Every module ends with ready-to-use
                frameworks that accelerate your first win.
              </p>
              <a href={`/${lng}/training`} className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 mt-4">
                Explore the Full Curriculum
              </a>
            </div>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {onboardingModules.map((module) => (
              <Reveal key={module.title} direction="up">
                <div className="bg-gray-900/60 border border-white/10 rounded-2xl p-6 h-full">
                  <p className="text-sm uppercase tracking-[0.35em] text-pink-200">{module.title}</p>
                  <p className="text-gray-200 mt-3">{module.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-20 md:py-24 bg-gray-900/60">
        <div className="container mx-auto px-4 md:px-8 grid gap-12 lg:grid-cols-[1.1fr_0.9fr] items-center">
          <div className="space-y-8">
            <Reveal direction="left">
              <h2 className="text-3xl md:text-4xl font-bold">Campaign analytics your clients will actually read</h2>
            </Reveal>
            <div className="space-y-6">
              {analyticsHighlights.map((highlight) => (
                <Reveal key={highlight.title} direction="up">
                  <div className="bg-gray-950/70 border border-white/10 rounded-2xl p-6">
                    <p className="text-xl font-semibold text-white">{highlight.title}</p>
                    <ul className="mt-3 space-y-2 text-gray-300 pl-4">
                      {highlight.bullets.map((bullet) => (
                        <li key={bullet} className="flex items-start gap-2">
                          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-teal-300" />
                          {bullet}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal direction="right">
            <img src={pic5} alt="Analytics dashboard preview" className="w-full rounded-3xl border border-white/5 shadow-2xl" />
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="container mx-auto px-4 md:px-8 space-y-12">
          <Reveal direction="up">
            <div className="text-center max-w-3xl mx-auto space-y-3">
              <p className="text-sm uppercase tracking-[0.4em] text-purple-300">Your Questions, Answered</p>
              <h2 className="text-3xl md:text-4xl font-bold">Partner Program FAQ</h2>
            </div>
          </Reveal>
          <div className="max-w-4xl mx-auto">
            {partnerFaqs.map((faq, index) => (
              <Reveal key={index} direction="up">
                <div className="border-b border-white/10">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex justify-between items-center text-left py-6"
                  >
                    <span className="text-lg font-semibold text-white">{faq.q}</span>
                    <motion.div
                      animate={{ rotate: openFaq === index ? 45 : 0 }}
                      transition={{ duration: 0.3, ease: 'easeOut' }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-purple-300">
                        <path d="M12 4V20M4 12H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </motion.div>
                  </button>
                  <motion.div
                    initial={false}
                    animate={{ height: openFaq === index ? 'auto' : 0, opacity: openFaq === index ? 1 : 0 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className="overflow-hidden"
                  >
                    <p className="text-gray-300 pb-6">{faq.a}</p>
                  </motion.div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-gray-950/70">
        <div className="container mx-auto px-4 md:px-8 text-center space-y-6">
          <Reveal direction="up">
            <p className="text-sm uppercase tracking-[0.4em] text-purple-300">Next steps</p>
          </Reveal>
          <Reveal direction="up" delay={0.1}>
            <h2 className="text-3xl md:text-4xl font-bold">
              Ready to build the next revenue stream for your agency?
            </h2>
          </Reveal>
          <Reveal direction="up" delay={0.2}>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Tell us about your client roster, we’ll show you the path from training to recurring commissions—with the same phygital
              experiences that already turn screen time into store traffic for our direct customers.
            </p>
          </Reveal>
          <Reveal direction="up" delay={0.3}>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href={`/${lng}/training`}
                className="inline-block text-purple-300 hover:text-purple-400 font-semibold transition-all duration-300 text-lg"
              >
                Start Free Training
              </a>

            </div>
          </Reveal>
        </div>
      </section>

      {isModalOpen && <ContactFormModal onClose={closeModal} />}
    </div>
  );
};

export default Partners;